import { SortableContainer } from "react-sortable-hoc";

// react-sortable-hoc 2.x guards continuous move events with React state. React 18
// can batch those updates, allowing handlePress to create several DOM helpers.
// Keep the workaround local to Settings; no form or list is remounted.
export const createSettingsSortableContainer = (Component) => {
    const Container = SortableContainer(Component);
    return class SettingsSortableContainer extends Container {
        constructor(props) {
            super(props);
            this.gestureActive = false;
            this.endingGesture = false;
            this.disposed = false;
            this.gestureVersion = 0;

            const press = this.handlePress;
            const end = this.handleSortEnd;
            const cancel = this.cancel;

            this.cancel = () => {
                // A mouseup can reach the container before the window listener,
                // while sorting:true is still queued. Preserve its active item.
                if (!this.gestureActive) cancel();
            };

            this.handlePress = (event) => {
                if (this.disposed || this.gestureActive || this.endingGesture) return Promise.resolve();
                // Recheck when the distance threshold is crossed: a request may
                // have started after mousedown armed this gesture.
                if (this.props.shouldCancelStart?.(event)) {
                    this.touched = false;
                    cancel();
                    return Promise.resolve();
                }
                this.helperRoot = this.helperContainer;
                this.clearVisuals();
                this.gestureActive = true;
                const version = ++this.gestureVersion;
                this.contentWindow?.addEventListener("blur", this.cancelGesture);
                return Promise.resolve(press(event)).then(() => {
                    if (this.disposed) { this.clearGesture(); return; }
                    if (version !== this.gestureVersion) return;
                    if (!this.helper?.parentNode && this.gestureActive) this.cancelGesture();
                }, (failure) => {
                    if (this.disposed) { this.clearGesture(); return; }
                    if (version === this.gestureVersion) this.cancelGesture(failure);
                });
            };

            this.handleSortEnd = (event) => {
                if (this.disposed || this.endingGesture || !this.gestureActive) return;
                if (event?.type === "touchcancel" || !this.manager.active || !this.helper?.parentNode) {
                    this.cancelGesture();
                    return;
                }
                this.endingGesture = true;
                try {
                    end(event);
                } catch (failure) {
                    this.cancelGesture(failure);
                } finally {
                    this.clearGesture();
                    this.endingGesture = false;
                }
            };

            this.cancelGesture = (failure) => {
                const wasActive = this.gestureActive;
                this.clearGesture();
                if (!this.disposed && wasActive) {
                    this.setState({ sorting: false, sortingIndex: null });
                    this.props.onSortCancel?.(failure instanceof Error ? failure : undefined);
                }
            };
        }

        clearVisuals() {
            this.helper?.remove();
            this.helperRoot?.querySelectorAll(".app-settings-sort-helper").forEach((helper) => helper.remove());
            Object.values(this.manager.refs).flat().forEach((ref) => {
                ref.edgeOffset = null;
                ref.boundingClientRect = null;
                ref.translate = null;
                const style = ref.node.style;
                style.opacity = "";
                style.visibility = "";
                ["", "Webkit", "Moz", "ms", "O"].forEach((prefix) => {
                    style[prefix ? `${prefix}Transform` : "transform"] = "";
                    style[prefix ? `${prefix}TransitionDuration` : "transitionDuration"] = "";
                });
            });
            this.helper = null;
            this.sortableGhost = null;
        }

        clearGesture() {
            this.gestureVersion += 1;
            ["touchmove", "mousemove"].forEach((name) => this.listenerNode?.removeEventListener(name, this.handleSortMove));
            ["touchend", "touchcancel", "mouseup"].forEach((name) => this.listenerNode?.removeEventListener(name, this.handleSortEnd));
            this.listenerNode?.removeEventListener("wheel", this.handleKeyEnd, true);
            this.listenerNode?.removeEventListener("mousedown", this.handleKeyEnd, true);
            this.listenerNode?.removeEventListener("keydown", this.handleKeyDown);
            this.contentWindow?.removeEventListener("blur", this.cancelGesture);
            clearTimeout(this.pressTimer);
            clearTimeout(this.cancelTimer);
            this.autoScroller?.clear();
            this.clearVisuals();
            this.listenerNode = null;
            this.manager.active = null;
            this.manager.isKeySorting = false;
            this.touched = false;
            this.gestureActive = false;
        }

        componentDidMount() {
            this.disposed = false;
            super.componentDidMount();
            // The installed HOC attaches listeners in a microtask for this
            // synchronous DOM container, even if it was already unmounted.
            Promise.resolve().then(() => {
                if (this.disposed) super.componentWillUnmount();
            });
        }

        componentWillUnmount() {
            this.disposed = true;
            this.clearGesture();
            super.componentWillUnmount();
        }
    };
};
